import { type MessageDescriptor, useIntl } from 'react-intl-next';

import { messages } from './messages';
import { type TValidatorFunction, type TValidatorReturnType } from './types';

export function isUndefined(value: any): value is undefined {
	return typeof value === 'undefined';
}

export function isArray<T>(value: any): value is Array<T> {
	return Array.isArray(value);
}

/**
 * Determines if a reference is a valid e-mail
 * according to the most simple validation rule
 * @see https://emailregex.com/
 *
 * @param value Reference to check.
 */
export const isEmail = (value: any): boolean => {
	const regExp = new RegExp(
		/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i,
	);
	return regExp.test(value);
};

/**
 * Determines if a reference is valid url
 *
 * @see https://gist.github.com/dperini/729294
 * @see https://mathiasbynens.be/demo/url-regex
 *
 * @param value Reference to check.
 */
export const isUrl = (value: any): boolean => {
	try {
		new URL(value);
	} catch (e) {
		return false;
	}
	return true;
};

export const toNumber = (value: any): number => Number(value).valueOf();

/**
 * A validator function to check if the provided value is filled. This should be
 * able to handle text based and multi select fields where the value is a string
 * or array respectively. For the multi select case, no selection counts as a
 * failure.
 * @param message The error message to display if the field is not filled
 */
export const isRequired =
	(message?: string): TValidatorFunction<any> =>
	({ formatMessage }) =>
	(value) => {
		if (value === null || typeof value === 'undefined' || value === '') {
			return message ?? formatMessage(messages.requiredMessage);
		}

		if (isArray(value) && value.length === 0) {
			return message ?? formatMessage(messages.requiredMessage);
		}
	};

/**
 * A validator function to check if the provided message is shorted than the
 * given limit.
 * @param limit Maximum number of characters accepted
 * @param message The error message to display if the limit is exceeded
 */
export const isMaxCharacterCount =
	(limit: number, message?: string): TValidatorFunction<string> =>
	({ formatMessage }) =>
	(value) =>
		isUndefined(value) || value.length <= limit
			? undefined
			: message ?? formatMessage(messages.validMaxLengthMessage);

export const useValidators = <V>(validators?: TValidatorFunction<V>[]) => {
	const { formatMessage } = useIntl();

	return (value?: V): TValidatorReturnType => {
		if (!validators?.length) {
			return;
		}

		return validators.reduce(
			(acc: Promise<string | undefined>, validator) =>
				acc.then((error) => error || validator({ formatMessage })(value)),
			Promise.resolve(undefined),
		);
	};
};

/**
 * A validator function to check if the provided value is a valid email
 * @param message The error message to display if email is invalid
 */
export const isValidEmail =
	(message?: string): TValidatorFunction<any> =>
	({ formatMessage }) =>
	(value) =>
		value === '' || isEmail(value)
			? undefined
			: message ?? formatMessage(messages.invalidEmailMessage);

/**
 * A validator function to check if the provided value is a valid url
 * @param message The error message to display if url is invalid
 */
export const isValidUrl =
	(message?: string): TValidatorFunction<any> =>
	({ formatMessage }) =>
	(value) =>
		isUndefined(value) || value === '' || isUrl(value)
			? undefined
			: message ?? formatMessage(messages.invalidUrlMessage);

export const matchesRegex =
	(regex: RegExp, message: MessageDescriptor): TValidatorFunction<string> =>
	({ formatMessage }) =>
	(value) =>
		!isUndefined(value) && regex.test(value) ? undefined : formatMessage(message);
